import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Field } from '@/pages/vault/VaultForms'
import {
  newAccountBootstrap,
  validAccountReference,
  AccountCommandError,
  type AccountBootstrap,
  type AccountMfaSetup,
} from '@/services/vault/accountEnrollment'
import {
  changeCommand,
  type ChangeProgress,
  type ChangePurpose,
} from '@/services/vault/accountChanges'

export default function AccountChangePage({ purpose }: { purpose: ChangePurpose }) {
  const manual = purpose === 'manual_recovery'
  const [email, setEmail] = useState(''),
    [accountEmail, setAccountEmail] = useState('')
  const [password, setPassword] = useState(''),
    [confirm, setConfirm] = useState(''),
    [code, setCode] = useState(''),
    [proof, setProof] = useState('')
  const [reference, setReference] = useState(''),
    [saved, setSaved] = useState(false)
  const [progress, setProgress] = useState<ChangeProgress | null>(null),
    [setup, setSetup] = useState<AccountMfaSetup | null>(null)
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [uncertain, setUncertain] = useState(false)
  const bootstrap = useRef<AccountBootstrap | null>(null)
  const pending = useRef<{
    operation: string
    body: AccountBootstrap & Record<string, unknown>
  } | null>(null)
  const running = useRef(false)
  const clear = () => {
    setPassword('')
    setConfirm('')
    setCode('')
    setProof('')
    setSetup(null)
  }
  async function run(
    operation: string,
    body: AccountBootstrap & Record<string, unknown>,
    remember = true
  ) {
    if (running.current) return
    running.current = true
    setBusy(true)
    setError('')
    if (remember) pending.current = { operation, body }
    try {
      const result = await changeCommand(operation, body, purpose)
      if ('secret' in result) {
        setSetup(result)
        setSaved(false)
        setProgress(p => (p ? { ...p, status: 'mfa_setup' } : p))
      } else {
        setProgress(result)
        if (result.status === 'complete' || result.status === 'dormant') {
          clear()
          pending.current = null
          setUncertain(false)
        }
      }
      if (remember) {
        pending.current = null
        setUncertain(false)
        setCode('')
        setProof('')
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request unavailable')
      if (remember) {
        const unknown = e instanceof AccountCommandError && e.uncertain
        setUncertain(unknown)
        if (!unknown) pending.current = null
      }
    } finally {
      running.current = false
      setBusy(false)
    }
  }
  function start() {
    const b = newAccountBootstrap()
    bootstrap.current = b
    setReference(`${b.command_id}.${b.bootstrap_secret}`)
    void run('start', {
      ...b,
      purpose,
      email,
      ...(manual ? { account_email: accountEmail } : { password, mfa_code: code }),
    })
  }
  function resume() {
    const [command_id, bootstrap_secret, extra] = reference.trim().split('.')
    if (
      extra ||
      !validAccountReference(command_id || '') ||
      !/^[a-f0-9]{64}$/.test(bootstrap_secret || '')
    ) {
      setError('Enter the complete private recovery reference.')
      return
    }
    bootstrap.current = { command_id, bootstrap_secret }
    clear()
    void run('status', { ...bootstrap.current }, false)
  }
  const state = progress?.status
  const finished = state === 'complete' || state === 'dormant'
  return (
    <main className="mx-auto max-w-2xl p-6 space-y-5 text-text-primary">
      <Link className="text-primary underline" to={manual ? '/login' : '/client-vault/account'}>
        Back to {manual ? 'sign in' : 'account'}
      </Link>
      <section className="card card-elevated p-6 space-y-4">
        <h1 className="text-2xl font-semibold">
          {manual
            ? 'Recover an account without MFA'
            : purpose === 'email_change'
              ? 'Change account email'
              : 'Replace authenticator'}
        </h1>
        <p>
          {manual
            ? 'Email access alone cannot recover this account. Two platform reviewers must independently verify ownership and the destination email. If ownership cannot be established, the account remains inaccessible and no documents are disclosed.'
            : 'Confirm your current password and authenticator or backup code, then verify the destination email. Completing the change signs out all sessions.'}
        </p>
        {manual && (
          <p className="text-sm text-text-secondary">
            Keep your private recovery reference safe. Share only the request ID with support.
            Manual review has 24 hours; approved recovery must finish within 30 minutes of the
            second review. A new sign-in or credential change invalidates this request.
          </p>
        )}
        {!progress && !uncertain && (
          <form
            className="space-y-4"
            onSubmit={e => {
              e.preventDefault()
              start()
            }}
          >
            {manual && (
              <Field
                label="Original account email"
                type="email"
                required
                value={accountEmail}
                onChange={e => setAccountEmail(e.target.value)}
              />
            )}
            <Field
              label={purpose === 'mfa_rotation' ? 'Current account email' : 'Destination email'}
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            {!manual && (
              <>
                <Field
                  label="Current password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                <Field
                  label="Current authenticator or backup code"
                  required
                  value={code}
                  onChange={e => setCode(e.target.value)}
                />
              </>
            )}
            <button className="btn btn-primary" disabled={busy}>
              Start verification
            </button>
          </form>
        )}
        {reference && (
          <>
            <label className="block text-sm">
              Private recovery reference (keep secret)
              <textarea
                className="input input-bordered w-full h-24 break-all"
                readOnly
                value={reference}
              />
            </label>
            <p className="text-sm break-all">
              Request ID for support: {bootstrap.current?.command_id}
            </p>
          </>
        )}
        {progress && <p role="status">Status: {state?.replace(/_/g, ' ')}</p>}
        {state === 'pending' && (
          <form
            className="space-y-3"
            onSubmit={e => {
              e.preventDefault()
              if (bootstrap.current)
                void run('verify-email', { ...bootstrap.current, email_proof: proof.trim() })
            }}
          >
            <Field
              label="Email verification proof"
              required
              value={proof}
              onChange={e => setProof(e.target.value)}
            />
            <button className="btn btn-primary" disabled={busy || uncertain}>
              Verify email
            </button>
            <button
              type="button"
              className="btn btn-secondary ml-2"
              disabled={busy || uncertain}
              onClick={() => bootstrap.current && void run('resend', { ...bootstrap.current })}
            >
              Send replacement proof
            </button>
          </form>
        )}
        {(state === 'approved' || state === 'email_verified' || state === 'mfa_setup') &&
          purpose !== 'email_change' &&
          !setup && (
            <button
              className="btn btn-primary"
              disabled={busy || uncertain}
              onClick={() => bootstrap.current && void run('mfa-setup', { ...bootstrap.current })}
            >
              Set up replacement authenticator
            </button>
          )}
        {setup && (
          <div className="space-y-3">
            <p>
              Add this secret to your authenticator, then save the new backup codes. The old
              authenticator remains active until you finish.
            </p>
            <code className="block break-all">{setup.secret}</code>
            <pre className="text-sm whitespace-pre-wrap">{setup.backup_codes.join('\n')}</pre>
            <label className="flex gap-2">
              <input type="checkbox" checked={saved} onChange={e => setSaved(e.target.checked)} />I
              saved my replacement backup codes securely.
            </label>
          </div>
        )}
        {((state === 'email_verified' && purpose === 'email_change') || setup) && (
          <form
            className="space-y-3"
            onSubmit={e => {
              e.preventDefault()
              if (manual && password !== confirm) {
                setError('Passwords must match.')
                return
              }
              if (bootstrap.current)
                void run('finish', { ...bootstrap.current, password, mfa_code: code.trim() })
            }}
          >
            <Field
              label={manual ? 'New password' : 'Current password'}
              type="password"
              autoComplete={manual ? 'new-password' : 'current-password'}
              required
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
            {manual && (
              <Field
                label="Confirm new password"
                type="password"
                required
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
              />
            )}
            <Field
              label={
                setup ? 'Replacement authenticator code' : 'Current authenticator or backup code'
              }
              required
              value={code}
              onChange={e => setCode(e.target.value)}
            />
            <button className="btn btn-primary" disabled={busy || uncertain || (!!setup && !saved)}>
              Finish and sign out all sessions
            </button>
          </form>
        )}
        {state === 'complete' && (
          <p>
            Your credentials have changed and previous sessions are signed out.{' '}
            <Link className="underline" to="/login">
              Sign in with your new credentials.
            </Link>
          </p>
        )}
        {state === 'dormant' && (
          <p>
            Ownership was not established. Recovery is closed; access has not been restored and no
            documents were disclosed.
          </p>
        )}
        {bootstrap.current && !finished && (
          <button
            className="btn btn-secondary"
            disabled={busy}
            onClick={() => bootstrap.current && void run('status', { ...bootstrap.current }, false)}
          >
            Check original request status
          </button>
        )}
        {uncertain && pending.current && !finished && (
          <button
            className="btn btn-secondary"
            disabled={busy}
            onClick={() =>
              pending.current && void run(pending.current.operation, pending.current.body)
            }
          >
            Retry exact original command
          </button>
        )}
        {error && (
          <p role="alert" className="text-red-600">
            {error}
          </p>
        )}
      </section>
      {!progress && !uncertain && (
        <form
          className="card p-6 space-y-3"
          onSubmit={e => {
            e.preventDefault()
            resume()
          }}
        >
          <h2 className="font-semibold">Resume an existing request</h2>
          <Field
            label="Private recovery reference"
            required
            value={reference}
            onChange={e => setReference(e.target.value)}
          />
          <button className="btn btn-secondary" disabled={busy}>
            Check saved request
          </button>
          <p className="text-sm">
            This page does not save passwords, MFA secrets, email proofs or your recovery reference
            in browser storage.
          </p>
        </form>
      )}
    </main>
  )
}
